/**
 * The public "claim this monastery" form.
 *
 * Deliberately thin, per the spec: this only records a claim request for
 * staff to review (server: routes/v1/claims.js → repositories/
 * partnerClaims.js). It never creates a login or a partner membership by
 * itself — approval issues the same staff-mediated onboarding invite the
 * admin console's "Issue invite" button does. There is no self-signup path
 * and this form does not open one.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CardHeading, EmptyState, Notice } from '../components/ui';
import Icon from '../components/Icon';
import { api } from '../lib/api';
import { trackEvent } from '../lib/consent';
import { formatPhoneNumber } from '../lib/format';
import { pickDharmaChallenge, checkDharmaAnswer } from '../lib/dharmaChallenge';
import { PRIORITY_COUNTRIES, OTHER_COUNTRIES } from '../lib/countries';

const RELATIONSHIPS = [
  { value: 'resident_monk_or_nun', label: 'Resident monk or nun' },
  { value: 'administrator', label: 'Administrator' },
  { value: 'authorized_representative', label: 'Authorized representative' },
  { value: 'other', label: 'Other' },
];

/** Dial codes for the diaspora communities GOMPA is starting with (North
 * America) plus the countries most of its monasteries are actually in —
 * not an exhaustive list of every country, just the ones a real claimant
 * is likely to need. "Other" falls back to a plain text field. */
const COUNTRY_CODES = [
  { code: '+1', flag: '🇺🇸', label: 'United States (+1)' },
  { code: '+1', flag: '🇨🇦', label: 'Canada (+1)' },
  { code: '+977', flag: '🇳🇵', label: 'Nepal (+977)' },
  { code: '+91', flag: '🇮🇳', label: 'India (+91)' },
  { code: '+975', flag: '🇧🇹', label: 'Bhutan (+975)' },
  { code: '+86', flag: '🇨🇳', label: 'China (+86)' },
  { code: '+44', flag: '🇬🇧', label: 'United Kingdom (+44)' },
  { code: '+61', flag: '🇦🇺', label: 'Australia (+61)' },
  { code: '', flag: '🌐', label: 'Other' },
];

const EMPTY_FORM = {
  claimantName: '', claimantRole: '', claimantEmail: '',
  countryCode: COUNTRY_CODES[0].label, phoneNumber: '',
  country: '', relationshipToMonastery: RELATIONSHIPS[0].value,
  claimantNote: '',
  challengeAnswer: '',
  // Honeypot — left blank by a real visitor; a filled value is rejected server-side.
  website: '',
};

const dialCodeFor = (label) => COUNTRY_CODES.find((c) => c.label === label)?.code ?? '';

export default function ClaimMonastery() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [monastery, setMonastery] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [state, setState] = useState({ status: 'idle', message: '' });
  // One question, chosen once when the page loads and kept for the visit.
  const [challenge] = useState(pickDharmaChallenge);

  useEffect(() => {
    const controller = new AbortController();
    api
      .monastery(id, controller.signal)
      .then(({ monastery: record }) => setMonastery(record))
      .catch((err) => err.name !== 'AbortError' && setNotFound(true));
    return () => controller.abort();
  }, [id]);

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  // Phone stays formatted as it is typed; changing the country re-groups the
  // digits already entered (NANP vs. plain groups).
  const setPhone = (event) =>
    setForm((prev) => ({
      ...prev,
      phoneNumber: formatPhoneNumber(event.target.value, dialCodeFor(prev.countryCode)),
    }));

  const setCountryCode = (event) =>
    setForm((prev) => ({
      ...prev,
      countryCode: event.target.value,
      phoneNumber: formatPhoneNumber(prev.phoneNumber, dialCodeFor(event.target.value)),
    }));

  const submit = async (event) => {
    event.preventDefault();

    if (!checkDharmaAnswer(challenge.id, form.challengeAnswer)) {
      setState({
        status: 'error',
        message: t('claim.challengeError', "That isn't quite the answer. Please try the question again."),
      });
      return;
    }

    setState({ status: 'sending', message: '' });
    const dialCode = dialCodeFor(form.countryCode);
    const { countryCode, phoneNumber, ...rest } = form;
    try {
      await api.submitClaim({
        ...rest,
        claimantPhone: `${dialCode} ${phoneNumber.trim()}`.trim(),
        challengeId: challenge.id,
        partnerId: monastery.id,
      });
      setState({
        status: 'done',
        message: t(
          'claim.successBody',
          "We've received your claim. We'll confirm it directly with the monastery's administration and follow up with you about next steps."
        ),
      });
      trackEvent('submit_claim', {
        item_id: monastery.id,
        relationship: form.relationshipToMonastery,
      });
    } catch (error) {
      setState({
        status: 'error',
        message: error.message || t('claim.errorBody', 'Could not submit this claim. Please try again.'),
      });
      trackEvent('claim_error', { item_id: monastery.id });
    }
  };

  if (notFound) {
    return (
      <div className="shell section">
        <EmptyState
          icon="alert"
          title={t('monasteryDetail.notFound', 'We could not find that monastery')}
          action={<Link to="/explore" className="btn btn--primary">{t('monasteryDetail.backToExplore', 'Back to Explore')}</Link>}
        />
      </div>
    );
  }

  if (!monastery) {
    return (
      <div className="shell section">
        <div className="skeleton" style={{ height: 240, borderRadius: 'var(--radius-lg)' }} />
      </div>
    );
  }

  return (
    <div className="shell shell--narrow" style={{ paddingBlock: 'var(--space-3xl) var(--space-5xl)' }}>
      <header className="stack" style={{ marginBottom: 'var(--space-2xl)' }}>
        <p className="eyebrow">{t('claim.eyebrow', 'Claim this monastery')}</p>
        <h1 className="display display--lg">
          {t('claim.title', { name: monastery.name, defaultValue: `Are you connected to ${monastery.name}?` })}
        </h1>
        <p className="lede">
          {t(
            'claim.lede',
            "Tell us who you are and how you're connected to the monastery. Before sending an onboarding invitation, GOMPA confirms every claim directly with the monastery's administration — submitting this form does not grant access on its own."
          )}
        </p>
      </header>

      {state.status === 'done' ? (
        <Notice tone="success" title={t('claim.successTitle', 'Claim received')}>
          {state.message}
        </Notice>
      ) : (
        <form className="card card__pad stack" onSubmit={submit}>
          <CardHeading icon="shield" title={t('claim.cardTitle', 'Your details')} />

          {state.status === 'error' && (
            <Notice tone="danger" title={t('claim.errorTitle', 'Not sent')}>{state.message}</Notice>
          )}

          <div className="field">
            <label className="field__label" htmlFor="claim-name">{t('claim.nameLabel', 'Full name')}</label>
            <input id="claim-name" className="input" required maxLength={120} value={form.claimantName} onChange={set('claimantName')} />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="claim-relationship">{t('claim.relationshipLabel', 'Relationship to the monastery')}</label>
            <select id="claim-relationship" className="select" value={form.relationshipToMonastery} onChange={set('relationshipToMonastery')}>
              {RELATIONSHIPS.map((r) => (
                <option key={r.value} value={r.value}>{t(`claim.relationships.${r.value}`, r.label)}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="claim-role">{t('claim.roleLabel', 'Your role')}</label>
            <input id="claim-role" className="input" required maxLength={120} placeholder={t('claim.rolePlaceholder', 'e.g. Abbot, Administrator, Secretary')} value={form.claimantRole} onChange={set('claimantRole')} />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="claim-email">{t('claim.emailLabel', 'Email')}</label>
            <input id="claim-email" type="email" className="input" required value={form.claimantEmail} onChange={set('claimantEmail')} />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="claim-phone">{t('claim.phoneLabel', 'Phone')}</label>
            <div className="row" style={{ gap: 'var(--space-sm)' }}>
              <select
                id="claim-phone-country"
                className="select"
                style={{ flex: '0 0 auto', width: 'auto', minWidth: 92 }}
                aria-label={t('claim.phoneCountryLabel', 'Country calling code')}
                value={form.countryCode}
                onChange={setCountryCode}
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.label} value={c.label}>{c.flag} {c.code || ''}</option>
                ))}
              </select>
              <input
                id="claim-phone"
                className="input"
                style={{ flex: 1 }}
                type="tel"
                inputMode="tel"
                required
                maxLength={20}
                autoComplete="tel-national"
                placeholder={t('claim.phonePlaceholder', 'e.g. 9841234567')}
                value={form.phoneNumber}
                onChange={setPhone}
              />
            </div>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="claim-country">{t('claim.countryLabel', 'Country')}</label>
            <select id="claim-country" className="select" required value={form.country} onChange={set('country')}>
              <option value="">{t('claim.countryPlaceholder', 'Select a country…')}</option>
              <optgroup label={t('claim.countryGroupCommon', 'Common')}>
                {PRIORITY_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </optgroup>
              <optgroup label={t('claim.countryGroupAll', 'All countries')}>
                {OTHER_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="Other">{t('claim.countryOther', 'Other')}</option>
              </optgroup>
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="claim-note">
              {t('claim.noteLabel', 'Anything we should know?')}{' '}
              <span className="field__hint" style={{ fontWeight: 400 }}>({t('common.optional', 'optional')})</span>
            </label>
            <textarea
              id="claim-note"
              className="textarea"
              maxLength={600}
              rows={3}
              placeholder={t('claim.notePlaceholder', 'e.g. who at the monastery we can contact to confirm, or anything unusual about your situation.')}
              value={form.claimantNote}
              onChange={set('claimantNote')}
            />
          </div>

          {/* Honeypot — hidden from sighted users and screen readers; a bot
              filling every field on the page fills this too. */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
            <label htmlFor="claim-website">Website</label>
            <input id="claim-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
          </div>

          <div className="claim-letter">
            <div className="claim-letter__head">
              <Icon name="journal" size={18} />
              <strong className="claim-letter__title">
                {t('claim.letterTitle', 'Prepare your authorization letter')}
              </strong>
            </div>
            <p className="claim-letter__body">
              {t('claim.letterBody', {
                name: monastery.name,
                defaultValue:
                  "You can get this ready now, while your claim is reviewed. Verification requires a letter on {{name}}'s official letterhead confirming that you are authorized to represent the monastery and are officially submitting this claim on its behalf on the GOMPA App.",
              })}
            </p>
            <ul className="claim-letter__list">
              <li>{t('claim.letterPoint1', "On the monastery's official letterhead, with its address and seal or stamp")}</li>
              <li>{t('claim.letterPoint2', 'States your name and role, and that you are authorized to act for the monastery')}</li>
              <li>{t('claim.letterPoint3', 'Signed by the monastery administration or the abbot')}</li>
            </ul>
            <p className="field__hint">
              {t('claim.letterHint', 'GOMPA staff will confirm where to send it once your claim reaches review.')}
            </p>
          </div>

          <div className="dharma-check">
            <div className="dharma-check__head">
              <Icon name="wheel" size={18} className="dharma-check__wheel" />
              <span className="dharma-check__eyebrow">
                {t('claim.challengeEyebrow', 'A moment of mindfulness')}
              </span>
            </div>
            <label className="dharma-check__prompt" htmlFor="claim-challenge">
              {t(`claim.challenges.${challenge.id}`, challenge.prompt)}
            </label>
            <select
              id="claim-challenge"
              className="select"
              required
              aria-describedby="claim-challenge-hint"
              value={form.challengeAnswer}
              onChange={set('challengeAnswer')}
            >
              <option value="">{t('claim.challengePlaceholder', 'Choose an answer…')}</option>
              {challenge.options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <p className="field__hint" id="claim-challenge-hint">
              {t('claim.challengeHint', 'Pick the answer from the list — it keeps spam claims out.')}
            </p>
          </div>

          <button className="btn btn--primary" disabled={state.status === 'sending'}>
            {state.status === 'sending' ? t('claim.sending', 'Submitting…') : t('claim.submitButton', 'Submit claim')}
          </button>
        </form>
      )}
    </div>
  );
}
